// A core's program as the core saw it: one row per pc it executed, with the
// most-populated bundle seen at that pc (the listing's variant), runs of empty pcs
// collapsed. `follow(bi)` keeps the row of the bundle at the cursor in view.
//
// The rows are virtual. A program of thirty thousand pcs is thirty thousand rows of
// a dozen spans each, which the DOM builds in seconds; so the listing keeps the rows
// as data, lays them out by measuring their text against the width it has (a bundle
// wraps onto a second line when its slots do not fit), and puts in the DOM only the
// rows that are on screen and a margin around them.

import { el } from "./dom.js";
import { slotUnit, unitColor } from "./isa.js";
import { splitSlot, slotColumns } from "./fmt.js";

const MARGIN = 12;        // rows rendered beyond each edge of the viewport

export class Listing {
  constructor(root, ctx, opts = {}) {
    this.ctx = ctx;
    this.opts = opts;
    this.root = root;
    root.classList.add("listing-wrap");
    this.spacer = el("div", "lspacer");
    root.appendChild(this.spacer);
    this.core = null;
    this.rows = [];                  // {pc, bi, count} or {pc, n} for a run of empty pcs
    this.top = new Float64Array(0);  // each row's y, and one past the last
    this.byPc = new Map();           // pc -> row index
    this.drawn = new Map();          // row index -> its element
    this.bi = -1;                    // the bundle last followed
    this.onRow = -1;
    this.metrics = null;
    root.addEventListener("scroll", () => this.render());
    if (typeof ResizeObserver !== "undefined") {
      this.ro = new ResizeObserver(() => { if (this.core) { this.layout(); this.render(); } });
      this.ro.observe(root);
    }
    // Text rendered later (a trace read through wasm) changes what the rows say and
    // how they wrap: lay out again, keep the followed row in view.
    this.off = ctx.disasm ? ctx.disasm.onLoad((core) => {
      if (core && core !== this.core) return;   // null: a pack's whole table landed
      this.cells.clear();
      this.layout();
      this.follow(this.bi, true);
    }) : null;
  }

  setCore(core) {
    if (core === this.core) return;
    this.core = core;
    this.rows = [];
    this.byPc.clear();
    this.cells = new Map();
    this.onRow = -1;
    for (const e of this.drawn.values()) e.remove();
    this.drawn.clear();
    if (!core) { this.spacer.style.height = "0px"; return; }
    const { model, disasm } = this.ctx;
    if (disasm && !core.hasText) disasm.prefetch(core, 0, core.n);
    const variants = model.pcVariants(core);
    const count = new Map();
    for (let i = 0; i < core.n; i++) count.set(core.pc[i], (count.get(core.pc[i]) || 0) + 1);
    const nopPcs = new Set();
    for (let k = 0; k < core.nopN.length; k++) for (let j = 0; j < core.nopN[k]; j++) nopPcs.add(core.nopPc[k] + j);
    const pcs = [...new Set([...variants.keys(), ...nopPcs])].sort((a, b) => a - b);
    let i = 0;
    while (i < pcs.length) {
      const pc = pcs[i];
      if (variants.has(pc)) {
        this.byPc.set(pc, this.rows.length);
        this.rows.push({ pc, bi: variants.get(pc), count: count.get(pc) || 0 });
        i++;
      } else {
        let j = i;
        while (j < pcs.length && !variants.has(pcs[j]) && pcs[j] === pc + (j - i)) j++;
        for (let k = i; k < j; k++) this.byPc.set(pcs[k], this.rows.length);
        this.rows.push({ pc, n: j - i });
        i = j;
      }
    }
    this.layout();
    this.root.scrollTop = 0;
    this.render();
  }

  // The slots of a row's bundle in listing order, split into mnemonic and operands;
  // null until the text is known.
  cellsOf(row) {
    let got = this.cells.get(row.pc);
    if (got !== undefined) return got;
    const { model, disasm } = this.ctx, core = this.core;
    const r = disasm ? disasm.get(core, row.bi) : { slots: model.slotsOf(core, row.bi) };
    if (!r) { this.cells.set(row.pc, null); return null; }
    const byUnit = new Map();
    for (const x of r.slots) { const { mn, ops } = splitSlot(core.kind, x.u, x.i, x); byUnit.set(x.u, { mn, ops, raw: x.i }); }
    got = slotColumns(core.kind, new Set(byUnit.keys())).map((u) => [u, byUnit.get(u)]);
    this.cells.set(row.pc, got);
    return got;
  }

  // The font's character widths and a row's line heights, measured once from the
  // page's own styles, so the layout below predicts what the browser will draw.
  measure() {
    if (this.metrics) return this.metrics;
    const probe = el("div", "lrow");
    probe.style.position = "absolute"; probe.style.visibility = "hidden"; probe.style.width = "10000px";
    const cell = el("span", "lcell"); cell.appendChild(el("span", "sn", "valu0")); cell.appendChild(el("span", "mn", "x")); probe.appendChild(cell);
    this.root.appendChild(probe);
    const one = probe.offsetHeight;
    const cs = getComputedStyle(cell);
    const cv = document.createElement("canvas").getContext("2d");
    cv.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    const cw = cv.measureText("0123456789abcdef").width / 16;
    const sn = probe.querySelector(".sn");
    const snw = sn.getBoundingClientRect().width;      // "valu0" and its margin
    const sep = el("span", "lsep", "│"); probe.appendChild(sep);
    const sepw = sep.getBoundingClientRect().width;
    probe.style.width = "10px";                          // force a second line
    const two = probe.offsetHeight;
    probe.remove();
    const m = { one, line: Math.max(1, two - one), cw, snPer: snw / 5, sepw, gap: 6, pad: 12, pcw: 44, countw: 36 };
    // Measured while the page was not laid out yet (the chip page opening from the
    // URL), everything reads 0: use a guess now and measure again on the first
    // resize, which is the panel getting its size.
    if (one <= 0 || cw <= 0) return { ...m, one: 18, line: 18, cw: 6.7, snPer: 5.5, sepw: 6.6 };
    return (this.metrics = m);
  }

  // Which line each of a row's slots lands on, packed left to right the way the
  // flex row wraps, and from that the row's height.
  linesOf(row, width, m) {
    if (row.n != null) return 1;
    const cells = this.cellsOf(row);
    if (!cells) return 1;
    let x = m.pad + m.pcw + m.gap + m.countw, lines = 1;
    cells.forEach(([u, c], k) => {
      const w = u.length * m.snPer + (c.mn.length + (c.ops ? 1 + c.ops.length : 0)) * m.cw;
      const items = k ? [m.sepw, w] : [w];
      for (const iw of items) {
        if (x + m.gap + iw > width && x > m.pad) { lines++; x = m.pad; }
        x += m.gap + iw;
      }
    });
    return lines;
  }

  layout() {
    const m = this.measure();
    const width = this.root.clientWidth || 1200;
    const n = this.rows.length;
    this.top = new Float64Array(n + 1);
    let y = 0;
    for (let i = 0; i < n; i++) {
      this.top[i] = y;
      const lines = this.linesOf(this.rows[i], width, m);
      this.rows[i].h = m.one + (lines - 1) * m.line;
      y += this.rows[i].h;
    }
    this.top[n] = y;
    this.spacer.style.height = y + "px";
    for (const [i, e] of this.drawn) { e.style.top = this.top[i] + "px"; e.style.height = this.rows[i].h + "px"; }
  }

  rowAt(y) {
    let lo = 0, hi = this.rows.length - 1;
    while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (this.top[mid] <= y) lo = mid; else hi = mid - 1; }
    return lo;
  }

  // The rows on screen and a margin around them are in the DOM, nothing else.
  render() {
    if (!this.core || !this.rows.length) return;
    const r = this.root, y0 = r.scrollTop, y1 = y0 + r.clientHeight;
    const a = Math.max(0, this.rowAt(y0) - MARGIN), b = Math.min(this.rows.length - 1, this.rowAt(y1) + MARGIN);
    for (const [i, e] of this.drawn) if (i < a || i > b) { e.remove(); this.drawn.delete(i); }
    for (let i = a; i <= b; i++) {
      let e = this.drawn.get(i);
      if (!e) { e = this.build(this.rows[i]); this.drawn.set(i, e); r.appendChild(e); }
      e.style.top = this.top[i] + "px";
      e.style.height = this.rows[i].h + "px";
      e.classList.toggle("on", i === this.onRow);
    }
  }

  build(row) {
    const e = el("div", "lrow" + (row.n != null ? " nop" : ""));
    e.appendChild(el("span", "lpc", String(row.pc)));
    if (row.n != null) {
      e.appendChild(el("span", "lcount", ""));
      e.appendChild(el("span", null, `${row.n} empty bundle${row.n === 1 ? "" : "s"}`));
      return e;
    }
    e.appendChild(el("span", "lcount", "×" + row.count));
    const cells = this.cellsOf(row);
    if (!cells) e.appendChild(el("span", "dim", "…"));
    else cells.forEach(([u, x], k) => {
      if (k) e.appendChild(el("span", "lsep", "│"));
      const c = el("span", "lcell");
      c.style.color = unitColor(slotUnit(u, x.raw).unit);
      c.appendChild(el("span", "sn", u));
      c.appendChild(el("span", "mn", x.mn));
      if (x.ops) c.appendChild(el("span", "ops", " " + x.ops));
      c.title = `${u}: ${x.raw}`;
      e.appendChild(c);
    });
    e.onclick = () => { if (this.opts.onPick) this.opts.onPick(this.core, row.pc); };
    return e;
  }

  // The pc of the row `dir` steps from the followed one, skipping runs of empty pcs.
  neighbourPc(dir) {
    let i = this.onRow < 0 ? (dir > 0 ? -1 : this.rows.length) : this.onRow;
    for (i += dir; i >= 0 && i < this.rows.length; i += dir) if (this.rows[i].bi != null) return this.rows[i].pc;
    return null;
  }

  follow(bi, force = false) {
    if (!this.core) return;
    this.bi = bi;
    const pc = bi >= 0 && bi < this.core.n ? this.core.pc[bi] : null;
    const i = pc == null ? -1 : (this.byPc.get(pc) ?? -1);
    this.onRow = i;
    if (i >= 0) {
      const r = this.root, top = r.scrollTop, h = r.clientHeight, y = this.top[i], y1 = y + this.rows[i].h;
      if (force || y < top + 20 || y1 > top + h - 20) r.scrollTop = Math.max(0, y - h / 2);
    }
    this.render();
  }
}
